import React, { useEffect, useRef } from "react";
import { FileDrop as ReactFileDrop } from "react-file-drop";
import styled from "styled-components";

import { Text } from "../components/Text";
import { Button } from "../components/tailwind";

const normalizeAllowedExtensions = (extensions: string[]): string[] => {
  return extensions.map((extension: string): string => {
    const t = extension.trim().toLowerCase();
    return t.startsWith(".") ? t : `.${t}`;
  });
};

const extensionsToAcceptAttribute = (
  extensions: string[]
): string | undefined => {
  if (extensions.length === 0) {
    return undefined;
  }
  return normalizeAllowedExtensions(extensions).join(",");
};

const fileMatchesAllowedExtensions = (
  file: File,
  normalizedExtensions: string[]
): boolean => {
  if (normalizedExtensions.length === 0) {
    return true;
  }
  const lastDot = file.name.lastIndexOf(".");
  if (lastDot === -1) {
    return false;
  }
  const ext = file.name.slice(lastDot).toLowerCase();
  return normalizedExtensions.includes(ext);
};

export const StyledReactFileDrop = styled(ReactFileDrop)`
  .file-drop-target {
    border: 1px dashed lightgrey;
    border-radius: 10px;
    padding: 3em 1em;
    margin: 0 2em 1em;
  }
`;

type FileDropProps = {
  selectedFile: File | null;
  isInvalid: boolean;
  onChange: (file: File | null) => void;
  contentUnselected?: React.ReactNode;
  contentSelected?: React.ReactNode;
};

export const FileDrop: React.FC<FileDropProps> = ({
  selectedFile,
  isInvalid,
  onChange,
  contentUnselected,
  contentSelected,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className="tw-text-center">
      <input
        data-testid="file-drop-input"
        onChange={(event): void => {
          const { files } = event.target;
          if (files.length > 0) {
            onChange(files.item(0));
          } else {
            onChange(null);
          }
          event.target.value = "";
        }}
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
      />
      {selectedFile ? (
        <div>
          {/* A file is selected */}
          <Text>Selected file: {selectedFile.name}</Text>
          {isInvalid && <Text color="red">Invalid file type.</Text>}
          {contentSelected}
          <Button
            type="button"
            data-testid="file-drop-remove-button"
            variant="secondary-alt"
            content={"Remove"}
            className="tw-mt-2 tw-ms-2"
            onClick={(): void => {
              if (fileInputRef.current) {
                fileInputRef.current.value = "";
              }
              onChange(null);
            }}
          />
        </div>
      ) : (
        <div data-testid="file-drop-target-wrapper">
          <StyledReactFileDrop
            onDrop={(files): void => {
              if (files.length > 0) {
                setTimeout(() => {
                  onChange(files.item(0));
                }, 200);
              } else {
                onChange(null);
              }
            }}
            onTargetClick={(): void => {
              fileInputRef.current?.click();
            }}
          >
            <Text as="h3">Drag your file to upload here.</Text>
            {contentUnselected}
          </StyledReactFileDrop>
          <Text color="grey" compact>
            or
          </Text>
          <Text>
            <Button
              type="button"
              variant="primary"
              content="Select file to upload"
              data-testid="file-drop-select-button"
              onClick={(): void => {
                fileInputRef.current?.click();
              }}
            />
          </Text>
        </div>
      )}
    </div>
  );
};

type MultiFileDropProps = {
  onAddFiles: (files: File[]) => void;
  content?: React.ReactNode;
  buttonOnly?: boolean;
};

export const MultiFileDrop: React.FC<MultiFileDropProps> = ({
  onAddFiles,
  content,
  buttonOnly = false,
}) => {
  const fileInputRef = useRef(null);

  return (
    <div
      className={buttonOnly ? "" : "tw-text-center"}
      data-testid="file-drop-target-wrapper"
    >
      <input
        data-testid="file-drop-input"
        onChange={(event): void => {
          const filelist = event.target.files;
          const files: File[] = [];
          for (let x = 0; x < filelist.length; x++) {
            files.push(filelist.item(x));
          }
          onAddFiles(files);
        }}
        ref={fileInputRef}
        type="file"
        className="tw-hidden"
        multiple
      />
      {!buttonOnly && (
        <>
          <StyledReactFileDrop
            onDrop={(filelist): void => {
              const files: File[] = [];
              for (let x = 0; x < filelist.length; x++) {
                files.push(filelist.item(x));
              }
              onAddFiles(files);
            }}
            onTargetClick={(): void => {
              fileInputRef.current.click();
            }}
          >
            <Text as="h3">Drag your file(s) to upload here.</Text>
            {content}
          </StyledReactFileDrop>
          <Text color="grey" compact>
            or
          </Text>
        </>
      )}
      <Text>
        <Button
          type="button"
          variant="primary"
          content="Select file(s) to upload"
          data-testid="file-drop-select-button"
          onClick={(): void => {
            fileInputRef.current.click();
          }}
        />
      </Text>
    </div>
  );
};

export type FileDropChip = {
  key: string;
  label: string;
  onRemove: () => void;
  /** Defaults to blue (files). Use secondary for the same gray as "New chat". */
  variant?: "blue" | "secondary";
};

type FileDropContainerProps = {
  selectedFiles: File[] | null;
  setSelectedFiles: (file: File[] | null) => void;
  children: React.ReactNode;
  maxFiles: number;
  menuOptions?: React.ReactNode;
  allowedFileExtensions?: string[];
  placeholderText?: string;
  extraChips?: FileDropChip[];
  /** Replaces the + button. Receives a callback that opens the file picker. */
  renderAddButton?: (openFilePicker: () => void) => React.ReactNode;
};

const SelectedChip: React.FC<{
  label: string;
  onRemove: () => void;
  variant?: "blue" | "secondary";
}> = ({ label, onRemove, variant = "blue" }) => (
  <div
    className={[
      "tw-w-fit",
      "tw-px-2",
      "tw-py-1",
      "tw-flex",
      "tw-items-center",
      "tw-gap-0.5",
      "tw-leading-none",
      "tw-rounded-md",
      "tw-text-nowrap",
      "tw-text-white",
      variant === "secondary" ? "tw-bg-secondary" : "tw-bg-textual-blue",
    ].join(" ")}
  >
    <span className="tw-leading-none">{label}</span>
    {/* `close` is in classic Material Icons; `close_small` is Symbols-only and renders blank. */}
    <button
      type="button"
      aria-label={`Remove ${label}`}
      onClick={(event): void => {
        event.preventDefault();
        event.stopPropagation();
        onRemove();
      }}
      className={[
        "tw-inline-flex",
        "tw-h-5",
        "tw-w-5",
        "tw-shrink-0",
        "tw-items-center",
        "tw-justify-center",
        "tw-border-0",
        "tw-bg-transparent",
        "tw-p-0",
        "tw-text-white",
        "hover:tw-cursor-pointer",
      ].join(" ")}
    >
      <i
        className="material-icons tw-pointer-events-none tw-text-[18px] tw-leading-none"
        aria-hidden
      >
        close
      </i>
    </button>
  </div>
);

export const FileDropContainer: React.FC<FileDropContainerProps> = ({
  selectedFiles,
  setSelectedFiles,
  children,
  maxFiles,
  menuOptions,
  allowedFileExtensions = [],
  placeholderText = "",
  extraChips = [],
  renderAddButton,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const normalizedExtensions = normalizeAllowedExtensions(
    allowedFileExtensions
  );
  const acceptAttribute = extensionsToAcceptAttribute(allowedFileExtensions);
  const hasFiles = Boolean(selectedFiles?.length);
  const hasChips = hasFiles || extraChips.length > 0;
  const fileCap = Math.max(0, maxFiles);

  const filesFromInputList = (fileList: FileList | File[]): File[] => {
    if (Array.isArray(fileList)) {
      return fileList;
    }
    const files: File[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const f = fileList.item(i);
      if (f) {
        files.push(f);
      }
    }
    return files;
  };

  const replaceFilesFromList = (fileList: FileList | File[]): void => {
    const filtered = filesFromInputList(fileList).filter((file) =>
      fileMatchesAllowedExtensions(file, normalizedExtensions)
    );
    const capped = filtered.slice(0, fileCap);
    setSelectedFiles(capped.length > 0 ? capped : null);
  };

  const mergeDroppedFilesFromList = (fileList: FileList | File[]): void => {
    const filtered = filesFromInputList(fileList).filter((file) =>
      fileMatchesAllowedExtensions(file, normalizedExtensions)
    );
    if (filtered.length === 0) {
      return;
    }
    const identity = (f: File): string =>
      `${f.name}\u0000${f.size}\u0000${f.lastModified}`;
    const existing = selectedFiles ?? [];
    const seen = new Set(existing.map(identity));
    const merged: File[] = [...existing];
    for (const f of filtered) {
      if (merged.length >= fileCap) {
        break;
      }
      const id = identity(f);
      if (!seen.has(id)) {
        seen.add(id);
        merged.push(f);
      }
    }
    const next = merged.slice(0, fileCap);
    setSelectedFiles(next.length > 0 ? next : null);
  };

  useEffect(() => {
    if (!hasFiles && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [hasFiles, selectedFiles]);

  return (
    <div>
      <input
        data-testid="file-drop-input"
        onChange={(event): void => {
          const { files } = event.target;
          if (files && files.length > 0) {
            replaceFilesFromList(files);
          } else {
            setSelectedFiles(null);
          }
          event.target.value = "";
        }}
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        accept={acceptAttribute}
      />
      <ReactFileDrop
        onDrop={(files): void => {
          if (files.length > 0) {
            setTimeout(() => {
              mergeDroppedFilesFromList(files);
            }, 200);
          }
        }}
      >
        {children}
        <div className="tw-flex tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-2">
            {renderAddButton ? (
              renderAddButton((): void => {
                fileInputRef.current?.click();
              })
            ) : (
              <Button
                onClick={(): void => {
                  fileInputRef.current?.click();
                }}
                type="button"
                variant="secondary-alt"
                data-testid="file-drop-select-button"
                compact="very"
                borderless
                iconOnly
              >
                <i className="material-icons tw-text-md tw-w-5">add</i>
              </Button>
            )}

            {!hasChips && placeholderText ? (
              <span className="tw-leading-none tw-text-secondary">
                {placeholderText}
              </span>
            ) : hasChips ? (
              <div
                className={[
                  "tw-flex",
                  "tw-flex-wrap",
                  "tw-items-center",
                  "tw-gap-4",
                ].join(" ")}
              >
                {selectedFiles?.map((file, index) => (
                  <SelectedChip
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    label={file.name}
                    onRemove={(): void => {
                      const next =
                        selectedFiles?.filter((_, i) => i !== index) ?? [];
                      setSelectedFiles(next.length ? next : null);
                    }}
                  />
                ))}
                {extraChips.map((chip) => (
                  <SelectedChip
                    key={chip.key}
                    label={chip.label}
                    onRemove={chip.onRemove}
                    variant={chip.variant}
                  />
                ))}
              </div>
            ) : null}
          </div>
          {menuOptions}
        </div>
      </ReactFileDrop>
    </div>
  );
};
